import { Trash2 } from 'lucide-react';
import { createColumnHelper } from '@tanstack/react-table';

import { useRegistries } from '@/react/portainer/registries/queries/useRegistries';
import { useUsers } from '@/portainer/users/queries';
import { useTeams } from '@/react/portainer/users/teams/queries';
import { Option as AccessOption } from '@/react/portainer/access-control/AccessManagement/PorAccessManagementUsersSelector';

import { Button } from '@@/buttons';
import type { Option as PortainerOption } from '@@/form-components/PortainerSelect';
import { Datatable } from '@@/datatables';
import { createPersistedStore } from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import { Link } from '@@/Link';

import { UserTeamSelector } from '../../UserTeamSelector';

import type { DockerRegistryFormData } from './types';

const accessStorageKey = 'docker-registry-access-table';
const accessSettingsStore = createPersistedStore(accessStorageKey);

type AccessRow = DockerRegistryFormData['accessRows'][0] & {
  registryName?: string;
};

const columnHelper = createColumnHelper<AccessRow>();

interface RegistryAccessDatatableProps {
  formData: { accessRows?: DockerRegistryFormData['accessRows'] };
  onChange: (
    key: 'accessRows',
    value: DockerRegistryFormData['accessRows']
  ) => void;
}

export function DockerRegistryAccessDatatable({
  formData,
  onChange,
}: RegistryAccessDatatableProps) {
  const accessRows = formData.accessRows || [];
  const registriesQuery = useRegistries({
    select: (registries) => registries.filter((r) => r.Id !== 0),
  });
  const registries = registriesQuery.data || [];

  const usersQuery = useUsers(false);
  const teamsQuery = useTeams();
  const users = (usersQuery.data || []).map<AccessOption>((u) => ({
    Type: 'user',
    Id: u.Id,
    Name: u.Username,
  }));
  const teams = (teamsQuery.data || []).map<AccessOption>((t) => ({
    Type: 'team',
    Id: t.Id,
    Name: t.Name,
  }));
  const userTeamOptions: PortainerOption<AccessOption>[] = [
    ...users,
    ...teams,
  ].map((v) => ({ value: v, label: v.Name }));

  const tableState = useTableState(accessSettingsStore, accessStorageKey);

  // Map registry IDs to names
  const registryMap = new Map(registries.map((r) => [r.Id, r.Name]));

  // Add registry names to rows for display
  const rowsWithNames: AccessRow[] = accessRows.map((row) => ({
    ...row,
    registryName: registryMap.get(row.registryId) || 'Unknown',
  }));

  function handleRemoveRows(items: AccessRow[]) {
    const idsToRemove = new Set(items.map((item) => item.registryId));
    const newAccessRows = accessRows.filter(
      (row) => !idsToRemove.has(row.registryId)
    );
    onChange('accessRows', newAccessRows);
  }

  function handleAccessChange(
    item: AccessRow,
    selection: readonly AccessOption[]
  ) {
    const newAccessRows = [...accessRows];
    const index = newAccessRows.findIndex(
      (row) => row.registryId === item.registryId
    );
    if (index !== -1) {
      const userIds = selection
        .filter((o) => o.Type === 'user')
        .map((o) => o.Id);
      const teamIds = selection
        .filter((o) => o.Type === 'team')
        .map((o) => o.Id);
      newAccessRows[index] = { ...newAccessRows[index], userIds, teamIds };
      onChange('accessRows', newAccessRows);
    }
  }

  return (
    <Datatable
      title="Registry access list"
      getRowId={(row) => String(row.registryId)}
      dataset={rowsWithNames}
      columns={getColumns(userTeamOptions, users, teams, handleAccessChange)}
      settingsManager={tableState}
      isRowSelectable={() => true}
      renderTableActions={(selectedRows) => (
        <TableActions selectedRows={selectedRows} onRemove={handleRemoveRows} />
      )}
      data-cy="docker-registry-access-datatable"
      emptyContentLabel="No registry access configured"
    />
  );
}

function TableActions({
  selectedRows,
  onRemove,
}: {
  selectedRows: AccessRow[];
  onRemove: (items: AccessRow[]) => void;
}) {
  return (
    <Button
      color="dangerlight"
      size="small"
      icon={Trash2}
      onClick={() => onRemove(selectedRows)}
      data-cy="remove-registry-access-button"
      disabled={selectedRows.length === 0}
    >
      Remove
    </Button>
  );
}

function getColumns(
  userTeamOptions: PortainerOption<AccessOption>[],
  users: AccessOption[],
  teams: AccessOption[],
  handleAccessChange: (
    item: AccessRow,
    selection: readonly AccessOption[]
  ) => void
) {
  return [
    columnHelper.accessor('registryName', {
      header: 'Registry',
      cell: ({ row }) => (
        <Link
          to={`/admin/registries/${row.original.registryId}`}
          data-cy={`registry-link-${row.original.registryId}`}
        >
          {row.original.registryName || 'Unknown'}
        </Link>
      ),
      meta: {
        className: 'w-1/3',
      },
    }),
    columnHelper.display({
      id: 'access',
      header: 'Access',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: ({ row }) => (
        <UserTeamSelector
          options={userTeamOptions}
          value={toOptions(row.original, users, teams)}
          onChange={(value) => handleAccessChange(row.original, value)}
        />
      ),
      meta: {
        className: 'w-2/3',
      },
    }),
  ];
}

function toOptions(
  row: AccessRow,
  users: AccessOption[],
  teams: AccessOption[]
): AccessOption[] {
  const userMap = new Map(users.map((u) => [u.Id, u]));
  const teamMap = new Map(teams.map((t) => [t.Id, t]));
  const userOpts = row.userIds
    .map((id) => userMap.get(id))
    .filter(Boolean) as AccessOption[];
  const teamOpts = row.teamIds
    .map((id) => teamMap.get(id))
    .filter(Boolean) as AccessOption[];
  return [...userOpts, ...teamOpts];
}
